import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ConfigProvider } from 'antd-mobile';
import { useLiff } from './hooks/useLiff';
import { useStore } from './store';
import ShopNameModal from './components/ShopNameModal';

// Pages
import HomePage from './pages/HomePage';
import CalculatorPage from './pages/CalculatorPage';
import SummaryPage from './pages/SummaryPage';
import QRPage from './pages/QRPage';
import ProfilePage from './pages/ProfilePage';
import MiniPosPage from './pages/MiniPosPage';
import OnboardingPage from './pages/OnboardingPage';
import SlipsPage from './pages/SlipsPage';
import UpgradePage from './pages/UpgradePage';
import StaffPage from './pages/StaffPage';
import JoinPage from './pages/JoinPage';
import TutorialPage from './pages/TutorialPage';

// Owner-only route guard
function OwnerRoute({ children }: { children: React.ReactNode }) {
    const dbUser = useStore((s) => s.dbUser);
    if (dbUser?.role === 'STAFF') {
        return <Navigate to="/" replace />;
    }
    return <>{children}</>;
}

// Layout
import { Layout } from './components/Layout';

// Loading Screen
import LoadingScreen from './components/LoadingScreen';

// Ant Design Mobile styles
import 'antd-mobile/es/global';

function App() {
    const { initLiff } = useLiff();
    const isReady = useStore((s) => s.isReady);
    const dbUser = useStore((s) => s.dbUser);
    const location = useLocation();
    const navigate = useNavigate();
    const [showShopNameModal, setShowShopNameModal] = useState(false);

    useEffect(() => {
        initLiff();
    }, [initLiff]);

    // Show shop name modal for owners who haven't set shop name yet
    useEffect(() => {
        if (dbUser && dbUser.role === 'OWNER' && !dbUser.shop_name) {
            setShowShopNameModal(true);
        }
    }, [dbUser]);

    // Global subscription expiry redirect for OWNERs
    useEffect(() => {
        if (!dbUser || dbUser.role === 'STAFF') return;
        if (['/upgrade', '/join'].includes(location.pathname)) return;
        if (dbUser.subscription_expires_at) {
            if (new Date(dbUser.subscription_expires_at) < new Date()) {
                navigate('/upgrade', { replace: true });
            }
        }
    }, [dbUser, location.pathname, navigate]);

    if (!isReady) {
        return <LoadingScreen />;
    }

    // Hide navbar on calculator and pos pages for full-screen experience
    const hideNav = location.pathname === '/calculator' ||
        location.pathname === '/pos' ||
        location.pathname === '/onboarding' ||
        location.pathname === '/slips' ||
        location.pathname === '/upgrade' ||
        location.pathname === '/staff' ||
        location.pathname === '/join' ||
        location.pathname === '/tutorial';

    return (
        <ConfigProvider>
            <Layout hideNav={hideNav}>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/calculator" element={<CalculatorPage />} />
                    <Route path="/summary" element={<SummaryPage />} />
                    <Route path="/qr" element={<QRPage />} />
                    <Route path="/profile" element={<OwnerRoute><ProfilePage /></OwnerRoute>} />
                    <Route path="/pos" element={<MiniPosPage />} />
                    <Route path="/onboarding" element={<OwnerRoute><OnboardingPage /></OwnerRoute>} />
                    <Route path="/slips" element={<SlipsPage />} />
                    <Route path="/upgrade" element={<OwnerRoute><UpgradePage /></OwnerRoute>} />
                    <Route path="/staff" element={<OwnerRoute><StaffPage /></OwnerRoute>} />
                    <Route path="/join" element={<JoinPage />} />
                    <Route path="/tutorial" element={<TutorialPage />} />
                </Routes>
            </Layout>
            <ShopNameModal
                visible={showShopNameModal}
                onClose={() => setShowShopNameModal(false)}
            />
        </ConfigProvider>
    );
}

export default App;

